import React, { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { supportAPI } from '../../../../api/client';
import { apiError } from '../../../../lib/host';
import { useT } from '../../../../lib/i18n';
import { ErrorBanner } from '../../ui';

export const MAX_MB = 10;
export const MAX_FILES = 5;
export const ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.csv,.json,.zip,.doc,.docx,.xls,.xlsx';
export const CATEGORIES = ['Getting Started', 'AI Receptionist', 'Phone Numbers', 'Calls & Conversations', 'Appointments',
  'Team Management', 'Billing & Plan', 'Settings & Troubleshooting', 'Other'];
export const sizeLabel = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Files picked for a ticket or reply: checked for size and count before upload. */
export function useAttachments(t, setError) {
  const [files, setFiles] = useState([]);
  const add = (list) => {
    const next = [...files];
    for (const f of list) {
      if (f.size > MAX_MB * 1048576) { setError(t('{name} is larger than 10 MB', { name: f.name })); continue; }
      if (next.length >= MAX_FILES) { setError(t('Attach up to {n} files', { n: MAX_FILES })); break; }
      next.push(f);
    }
    setFiles(next);
  };
  return { files, add, remove: (i) => setFiles(files.filter((_, n) => n !== i)), clear: () => setFiles([]) };
}

export function FileChips({ files, onRemove }) {
  return files.length ? (
    <div className="hs-chips">{files.map((f, i) => (
      <span key={`${f.name}-${i}`} className="hs-chip"><Paperclip size={12} />{f.name}<small>{sizeLabel(f.size)}</small>
        <button type="button" aria-label={`Remove ${f.name}`} onClick={() => onRemove(i)}><X size={12} /></button></span>
    ))}</div>
  ) : null;
}

/** Open a Support Ticket: subject, category, description and optional files. */
export default function TicketForm({ onCreated, formRef }) {
  const { t } = useT();
  const [form, setForm] = useState({ subject: '', category: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const picker = useRef(null);
  const attach = useAttachments(t, setError);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v.trim()));
    attach.files.forEach((f) => data.append('files', f));
    try {
      const res = await supportAPI.create(data);
      setForm({ subject: '', category: '', description: '' });
      attach.clear();
      onCreated(res.data);
    } catch (err) {
      setError(apiError(err, t('Could not send your ticket. Please try again.')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel hs-panel" ref={formRef}>
      <div className="hs-panel-head"><div><h2>{t('Open a Support Ticket')}</h2><p>{t('Submit a request and our support team will get back to you.')}</p></div></div>
      <form className="hs-form" onSubmit={submit}>
        <ErrorBanner message={error} />
        <div className="hs-form-row">
          <label className="hs-field"><span>{t('Subject')} <i>*</i></span>
            <input value={form.subject} maxLength={200} required placeholder={t('Briefly describe your issue (e.g. Call not working)')} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></label>
          <label className="hs-field"><span>{t('Category')} <i>*</i></span>
            <select value={form.category} required onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="">{t('Select category')}</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}
            </select></label>
        </div>
        <label className="hs-field"><span>{t('Description')} <i>*</i></span>
          <textarea value={form.description} rows={4} minLength={10} maxLength={10000} required placeholder={t('Please provide detailed information about your issue...')}
            onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
        <div className="hs-attach" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); attach.add([...e.dataTransfer.files]); }}>
          <Paperclip size={22} />
          <span><b>{t('Attach Files')}</b> <em>({t('Optional')})</em><small>{t('You can upload screenshots, logs or documents (Max 10 MB each)')}</small></span>
          <button type="button" className="secondary-action" onClick={() => picker.current?.click()}>{t('Choose Files')}</button>
          <input ref={picker} type="file" multiple hidden accept={ACCEPT} onChange={(e) => { attach.add([...e.target.files]); e.target.value = ''; }} />
        </div>
        <FileChips files={attach.files} onRemove={attach.remove} />
        <div className="hs-submit"><button className="primary-action" disabled={busy}>{busy ? t('Sending…') : t('Submit Ticket')}</button></div>
      </form>
    </section>
  );
}
